import {
  format,
  subMonths,
  differenceInMonths,
  differenceInDays,
} from "date-fns";
import { DateRangeFilter } from "@cma/shared/apis/comps/cma/generated";

const rangeDatesListingStatusOptions = [
  {
    label: "Last 1 day",
    value: '{"D": -1}',
  },
  {
    label: "Last 7 days",
    value: '{"D": -7}',
  },
  {
    label: "Last 30 days",
    value: '{"D": -30}',
  },
  {
    label: "Last 60 days",
    value: '{"D": -60}',
  },
  {
    label: "Last 3 months",
    value: '{"M": -3}',
  },
  {
    label: "Last 6 months",
    value: '{"M": -6}',
  },
  {
    label: "Last 12 months",
    value: '{"M": -12}',
  },
  {
    label: "Last 24 months",
    value: '{"M": -24}',
  },
  {
    label: "Last 36 months",
    value: '{"M": -36}',
  },
  {
    label: "Any Time",
    value: "all-time",
  },
];

const isToday = (date: Date): boolean => {
  const today = new Date();
  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
};

const findMatchingRelativeOption = (startDate: Date, endDate: Date) => {
  if (!isToday(endDate)) return null;

  const months = differenceInMonths(endDate, startDate);
  const days = differenceInDays(endDate, startDate);

  return rangeDatesListingStatusOptions.find((option) => {
    if (option.value === "all-time") return false;
    try {
      const parsed = JSON.parse(option.value);
      return (
        (parsed.M && Math.abs(parsed.M) === months) ||
        (parsed.D && Math.abs(parsed.D) === days)
      );
    } catch {
      return false;
    }
  });
};

export const convertDateRangeToRelativeString = (
  dateRange: DateRangeFilter
): string => {
  if (dateRange.startAt === null && dateRange.endAt === null) {
    return "all-time";
  }

  if (dateRange.startAt && dateRange.endAt) {
    const startDate = new Date(dateRange.startAt);
    const endDate = new Date(dateRange.endAt);

    const matchingOption = findMatchingRelativeOption(startDate, endDate);
    if (matchingOption) return matchingOption.value;
  }

  return `custom|${dateRange.startAt?.toISOString() || "null"}|${dateRange.endAt?.toISOString() || "null"}`;
};

export const convertRelativeStringToDateRange = (
  relativeString: string
): DateRangeFilter | null => {
  if (!relativeString) return null;
  if (relativeString === "all-time") return { startAt: null, endAt: null };

  try {
    const parsed = JSON.parse(relativeString);
    if (parsed.M) {
      const endDate = new Date();
      const startDate = subMonths(endDate, Math.abs(parsed.M));
      return { startAt: startDate, endAt: endDate };
    }
    if (parsed.D) {
      const endDate = new Date();
      const startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - Math.abs(parsed.D));
      return { startAt: startDate, endAt: endDate };
    }
  } catch {
    if (relativeString.startsWith("custom|")) {
      const parts = relativeString.replace("custom|", "").split("|");
      if (parts.length >= 2) {
        const startAt = parts[0] === "null" ? null : new Date(parts[0]);
        const endAt = parts[1] === "null" ? null : new Date(parts[1]);
        return { startAt, endAt };
      }
    }
  }

  return null;
};

export const getDateRangeDisplayLabel = (
  startDate: Date,
  endDate: Date
): string => {
  const matchingOption = findMatchingRelativeOption(startDate, endDate);

  if (matchingOption) {
    return matchingOption.label.toLowerCase();
  }

  return `${format(startDate, "yyyy-MM-dd")} - ${format(endDate, "yyyy-MM-dd")}`;
};

export const createDynamicDateOptions = (
  currentValue?: DateRangeFilter | null
) => {
  const baseOptions = [...rangeDatesListingStatusOptions];
  if (!currentValue) return baseOptions;

  const currentValueAsString = convertDateRangeToRelativeString(currentValue);
  if (baseOptions.some((option) => option.value === currentValueAsString)) {
    return baseOptions;
  }

  let dynamicLabel = "Custom Selection";

  if (currentValue.startAt && currentValue.endAt) {
    const startDateStr = format(new Date(currentValue.startAt), "MMM dd, yyyy");
    const endDateStr = format(new Date(currentValue.endAt), "MMM dd, yyyy");
    dynamicLabel = `${startDateStr} - ${endDateStr}`;
  } else if (currentValue.startAt && !currentValue.endAt) {
    const startDateStr = format(new Date(currentValue.startAt), "MMM dd, yyyy");
    dynamicLabel = `Since ${startDateStr}`;
  } else if (!currentValue.startAt && currentValue.endAt) {
    const endDateStr = format(new Date(currentValue.endAt), "MMM dd, yyyy");
    dynamicLabel = `Before ${endDateStr}`;
  } else if (currentValue.startAt === null && currentValue.endAt === null) {
    dynamicLabel = "Any Time";
  }

  return [{ label: dynamicLabel, value: currentValueAsString }, ...baseOptions];
};
